import React, { useEffect, useState } from 'react';
import { t } from '@lingui/macro';

import { i18n } from '@lingui/core';
import Panel from '../../page-framework/panel/PanelDecorator';
import { panelPropDefaults, panelPropTypes } from '../../page-framework/panel/PanelPropTypes';
import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import { useQueryNowLoader } from '../../data-table/Loaders';
import { filterId as testFilterId } from '../../filters/test-filter/TestFilter';
import { businessServiceFilterId, siteFilterId, timeRangeFilterId } from '../../filters';
import { Ng1DrillDownButton } from '../../ng1-drill-down/Ng1DrillDownButton';
import useNg1DrillDownService from '../../../services/Ng1DrillDownService';
import { FORMATTER_TYPES } from '../../../utilities/FormattingUtilities';
import { SELECTIONMODE_MULTI } from '../../data-table/SelectionContext';
import ReportingDataTable from '../../data-table/ReportingDataTable';
import { testAgentsParser } from './TestAgentsParserFilter';
import { useDataService } from '../../../services/ApiDataService';
import { renderAgents } from './WebTestNowHelpers';
import { useColumnStyles } from './WebTestNowStyles';

const title = t`Service Tests`;

let dataSource = {
    url: '/ipm/v1/admin/quicktests',
    totalProperty: 'count',
    dataProperty: 'data',
    isResultsPage: false,
    numericMetrics: ['numAgents'],
    metricsToNotSort: ['createdAt'],
    params: {
        searchFields: ['status', 'name', 'createdBy'],
        query: JSON.stringify({ type: '', verify: { $ne: true } }),
    },
};

const WIRELESS = 'wireless';
const WIRED = 'wired';
const DEFAULT = 'default';
const MULTI = 'multi';

export const SERVICE_TEST_INTERFACE_LOOKUP = {
    [WIRED]: i18n._(t`Wired`),
    [WIRELESS]: i18n._(t`Wireless`),
    [DEFAULT]: i18n._(t`Wired`),
    [MULTI]: i18n._(t`Wireless (Part of a Wi-Fi Group Test)`),
};

function getDefaultColumns(classes) {
    return [
        {
            type: COLUMN_TYPES.LINK,
            title: t`Run Date/Time`,
            mapping: 'createdAt',
            sortable: true,
            width: 250,
            formatter: FORMATTER_TYPES.DATETIME,
            link: {
                target: `/quickTests/web/:_id`,
                mappedFilterParams: {
                    [testFilterId]: 'test',
                },
            },
            csvFormatter: (value, row) => row.createdAt,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Status`,
            mapping: 'status',
            resizable: false,
            width: 150,
            csvFormatter: (value, row) => row.status,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Test`,
            sortable: true,
            mapping: 'name',
            width: 250,
            csvFormatter: (value, row) => row.name,
        },
        {
            title: t`Interface`,
            width: 100,
            mapping: 'adapterType',
            formatter: (value, row) => serviceTestInterfaceFormatter(row.adapterType),
            csvFormatter: (value, row) => serviceTestInterfaceFormatter(row.adapterType),
            sortable: true,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Created By`,
            mapping: 'createdBy',
            sortable: true,
            csvFormatter: (value, row) => row.createdBy,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Test From Locations`,
            width: 180,
            className: classes.root,
            mapping: 'agents',
            sortable: true,
            formatter: (value, row) => testAgentsParser(row.agents),
            csvFormatter: (value, row) => testAgentsParser(row.agents, true, 1).agentText,
            renderer: renderAgents,
        },
    ];
}

function serviceTestInterfaceFormatter(adapterType) {
    return SERVICE_TEST_INTERFACE_LOOKUP[adapterType];
}

export function buildAgentTemplate() {
    let template = '<% var column = data.column, row = data.row, mapping = column.mapping;';
    template += 'var value = column.formatter(column.mapping(row), true); %>';
    template += '<%= value.agentText %>';
    template += '<% if (value.agentHelpText) { %>';
    template +=
        '<i class="material-icons help" style="padding-left: 4px; font-size: 18px; cursor: default;" title="<%=value.agentHelpText %>">help_outline</i></span>';
    template += '<% } %>';

    return template;
}

const sorters = [{ property: 'createdAt', direction: 'desc' }];

const NG1_DRILLDOWN_COLUMN = {
    type: COLUMN_TYPES.ICON,
    mapping: 'test',
    width: 30,
    uiOnly: true,
    icons: [
        {
            getIcon: (row) => {
                return <Ng1DrillDownButton test={row?.test} />;
            },
            getTooltip: () => t`Launch nGeniusONE Smart Edge Service Monitor`,
        },
    ],
};

function WebTestNowTestListPanel({
    tableState,
    setTableState,
    tableSelection,
    setTableSelection,
    setDeleteDisabled,
    setReRunDisabled,
    triggerRef,
    ...props
}) {
    const classes = useColumnStyles();
    const loader = useQueryNowLoader(dataSource);
    const { hasNg1DrillDownPrivilege } = useNg1DrillDownService();
    const [columns, setColumns] = useState(getDefaultColumns(classes));
    const dataService = useDataService();

    const handleTableSelectionChange = (currentTableSelection) => {
        setTableSelection(currentTableSelection);
        setDeleteDisabled(
            !currentTableSelection || (!currentTableSelection.selection.length && !currentTableSelection.inverted)
        );

        // currentTableSelection.inverted is true when the very top box is checked that selects everything.
        const oneTestSelected = currentTableSelection && currentTableSelection.selection.length === 1;
        setReRunDisabled(!oneTestSelected || currentTableSelection.inverted);
    };

    useEffect(() => {
        if (hasNg1DrillDownPrivilege()) {
            setColumns([...getDefaultColumns(classes), NG1_DRILLDOWN_COLUMN]);
        }
    }, [hasNg1DrillDownPrivilege, classes]);

    useEffect(() => {
        const TEST_TYPE_URL = '/ipm/v1/open/testTypes';
        const params = {
            query: JSON.stringify({ name: 'Web' }),
        };
        dataService.get(TEST_TYPE_URL, params).then((response) => {
            const testTypeId = response[0]._id;
            const dataSourceQueryParams = {
                searchFields: ['status', 'name', 'createdBy'],
                query: JSON.stringify({ type: testTypeId, verify: { $ne: true } }),
            };
            dataSource = {
                ...dataSource,
                params: dataSourceQueryParams,
            };
        });
    }, [dataService]);

    return (
        <ReportingDataTable
            panelProps={props}
            loader={loader}
            columns={columns}
            sorters={sorters}
            visibleRows={0}
            deletedMapping="test.deleted"
            showSelectionColumn
            onStateChanged={setTableState}
            triggerRef={triggerRef}
            selectionOptions={{
                mode: SELECTIONMODE_MULTI,
                allowSelectAll: true,
                idMapping: '_id',
                onChange: handleTableSelectionChange,
            }}
            disableColumnRemover
        />
    );
}

WebTestNowTestListPanel.propTypes = panelPropTypes;

WebTestNowTestListPanel.defaultProps = {
    ...panelPropDefaults,
    title,
    autoHeight: true,
    minContentHeight: 130,
};

export default Panel({
    typeAlias: 'WebTestList',
    filters: {
        required: [timeRangeFilterId],
        optional: [businessServiceFilterId, siteFilterId],
    },
})(WebTestNowTestListPanel);
